import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { supabase } from '../lib/supabase';
import logoDark from '../assets/images/etenant-logo-dark.png';

type PortalState = 'verifying' | 'verified';

interface PaymentRecord {
  id: string;
  amount: number;
  payment_type: string;
  payment_method: string | null;
  due_date: string | null;
  status: string;
  notes: string | null;
  created_at: string;
}

interface AgreementRecord {
  id: string;
  rental_agreement_no: string | null;
  start_date: string;
  end_date: string | null;
  monthly_rent: number;
  is_active: boolean;
  is_cancelled: boolean;
  is_archived: boolean;
}

interface MaintenanceRequestRecord {
  id: string;
  maintenance_request_no: string | null;
  title: string;
  description: string;
  status: string;
  category: string;
  priority: string;
  estimated_cost: number | null;
  actual_cost: number | null;
  created_at: string;
  updated_at: string;
  photo_storage_path: string | null;
}

interface ChecklistItemRecord {
  id: string;
  section: string;
  is_checked: boolean;
}

interface ChecklistRecord {
  id: string;
  checklist_type: 'move_in' | 'move_out';
  items: ChecklistItemRecord[];
}

interface PortalData {
  agreement: AgreementRecord;
  tenant: { first_name: string; last_name: string };
  payments: PaymentRecord[];
  payments_total_count: number;
  maintenance_requests: MaintenanceRequestRecord[];
  maintenance_requests_total_count: number;
  checklists: ChecklistRecord[];
}

interface PageResult<T> {
  items: T[];
  total_count: number;
  page: number;
  page_size: number;
}

// Tenant Access Link is Premium & Pro only — the portal RPCs return
// 'plan_required' when the landlord is on the Free plan (or it has expired).
const PLAN_REQUIRED_POPUP = {
  icon: 'fas fa-lock',
  iconColor: '#d97706',
  title: 'Link Unavailable',
  message: "This tenant portal isn't available on your landlord's current plan. Please contact your landlord for assistance.",
};

const SESSION_KEY_PREFIX = 'tenant_portal_';
const MAX_ATTEMPTS = 5;
const PAGE_SIZE = 10;

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  paid:      { bg: '#dcfce7', text: '#16a34a', label: 'Paid' },
  pending:   { bg: '#fef3c7', text: '#d97706', label: 'Pending' },
  overdue:   { bg: '#fee2e2', text: '#dc2626', label: 'Overdue' },
  cancelled: { bg: '#f3f4f6', text: '#6b7280', label: 'Cancelled' },
};

const maintenanceStatusColors: Record<string, { bg: string; text: string; label: string }> = {
  pending:     { bg: '#fef3c7', text: '#d97706', label: 'Pending' },
  in_progress: { bg: '#fef3c7', text: '#d97706', label: 'In Progress' },
  completed:   { bg: '#dcfce7', text: '#16a34a', label: 'Completed' },
  cancelled:   { bg: '#f3f4f6', text: '#6b7280', label: 'Cancelled' },
};

const maintenanceCategoryIcons: Record<string, string> = {
  plumbing: 'fa-wrench',
  electrical: 'fa-bolt',
  hvac: 'fa-fan',
  appliance: 'fa-plug',
  structural: 'fa-house',
  pest_control: 'fa-bug',
  cleaning: 'fa-broom',
  landscaping: 'fa-tree',
  security: 'fa-shield-halved',
  other: 'fa-wrench',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function toTitleCase(str: string): string {
  return str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) =>
    txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  );
}

function getMaintenancePhotoUrl(storagePath: string): string {
  return supabase.storage.from('maintenance-request-images').getPublicUrl(storagePath).data.publicUrl;
}

const TenantPortal: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [state, setState] = useState<PortalState>('verifying');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [portalData, setPortalData] = useState<PortalData | null>(null);
  const [activeTab, setActiveTab] = useState<'payments' | 'maintenance' | 'checklists'>('payments');
  const [checklistSubTab, setChecklistSubTab] = useState<'move_in' | 'move_out'>('move_in');
  const [popup, setPopup] = useState<{ icon: string; iconColor: string; title: string; message: string } | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [paymentsTotalCount, setPaymentsTotalCount] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPageLoading, setPaymentsPageLoading] = useState(false);

  const [maintenanceRequests, setMaintenanceRequests] = useState<MaintenanceRequestRecord[]>([]);
  const [maintenanceTotalCount, setMaintenanceTotalCount] = useState(0);
  const [maintenancePage, setMaintenancePage] = useState(1);
  const [maintenancePageLoading, setMaintenancePageLoading] = useState(false);

  // Applies a fresh tenant_portal_verify response: agreement/tenant summary
  // plus page 1 of both paginated lists.
  const applyPortalData = (data: PortalData) => {
    setPortalData(data);
    setPayments(data.payments);
    setPaymentsTotalCount(data.payments_total_count);
    setPaymentsPage(1);
    setMaintenanceRequests(data.maintenance_requests);
    setMaintenanceTotalCount(data.maintenance_requests_total_count);
    setMaintenancePage(1);
  };

  useEffect(() => {
    if (!slug) {
      setPopup({
        icon: 'fas fa-link-slash',
        iconColor: '#d1d5db',
        title: 'Link Not Found',
        message: 'This link is invalid or has been disabled. Please contact your landlord for a new link.',
      });
      return;
    }
    const cached = sessionStorage.getItem(`${SESSION_KEY_PREFIX}${slug}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        // Silently re-verify the slug is still valid before trusting the cache
        supabase.rpc('tenant_portal_verify', {
          p_slug: slug,
          p_first_name: parsed.tenant.first_name,
          p_last_name: parsed.tenant.last_name,
        }).then(({ data, error }) => {
          if (error || data?.error) {
            // Slug no longer valid — clear cache and force re-verification
            sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${slug}`);
            if (data?.error === 'plan_required') {
              setPopup(PLAN_REQUIRED_POPUP);
              return;
            }
            setPopup({
              icon: 'fas fa-link-slash',
              iconColor: '#d1d5db',
              title: 'Link Not Found',
              message: 'This link is invalid or has been disabled. Please contact your landlord for a new link.',
            });
          } else {
            // Refresh cache with latest data
            sessionStorage.setItem(`${SESSION_KEY_PREFIX}${slug}`, JSON.stringify(data));
            applyPortalData(data);
            setState('verified');
          }
        });
      } catch {
        sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${slug}`);
      }
    }
  }, [slug]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (attempts >= MAX_ATTEMPTS) return;

    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('tenant_portal_verify', {
        p_slug: slug,
        p_first_name: firstName.trim(),
        p_last_name: lastName.trim(),
      });

      if (error) {
        setPopup({
          icon: 'fas fa-circle-exclamation',
          iconColor: '#ef4444',
          title: 'Something went wrong',
          message: 'Please try again later.',
        });
        setLoading(false);
        return;
      }

      if (data?.error === 'not_found') {
        setPopup({
          icon: 'fas fa-link-slash',
          iconColor: '#d1d5db',
          title: 'Link Not Found',
          message: 'This link is invalid or has been disabled. Please contact your landlord for a new link.',
        });
        setLoading(false);
        return;
      }

      if (data?.error === 'plan_required') {
        setPopup(PLAN_REQUIRED_POPUP);
        setLoading(false);
        return;
      }

      if (data?.error === 'invalid_name') {
        const next = attempts + 1;
        setAttempts(next);
        if (next >= MAX_ATTEMPTS) {
          setPopup({
            icon: 'fas fa-triangle-exclamation',
            iconColor: '#ef4444',
            title: 'Too Many Attempts',
            message: 'Please contact your landlord for assistance.',
          });
        } else {
          setPopup({
            icon: 'fas fa-triangle-exclamation',
            iconColor: '#d97706',
            title: 'Name Does Not Match',
            message: `${MAX_ATTEMPTS - next} attempt${MAX_ATTEMPTS - next === 1 ? '' : 's'} remaining.`,
          });
        }
        setLoading(false);
        return;
      }

      sessionStorage.setItem(`${SESSION_KEY_PREFIX}${slug}`, JSON.stringify(data));
      applyPortalData(data);
      setState('verified');
    } catch {
      setPopup({
        icon: 'fas fa-circle-exclamation',
        iconColor: '#ef4444',
        title: 'Something went wrong',
        message: 'Please try again later.',
      });
      setLoading(false);
    }
  };

  const agreement = portalData?.agreement;
  const tenant = portalData?.tenant;
  const checklists = portalData?.checklists ?? [];
  const activeChecklist = checklists.find((c) => c.checklist_type === checklistSubTab);

  const portalStatus = agreement
    ? agreement.is_cancelled
      ? 'Cancelled'
      : agreement.is_archived
      ? 'Completed'
      : agreement.is_active
      ? 'Active'
      : 'Inactive'
    : '';

  const portalStatusColor = portalStatus === 'Active'
    ? { bg: '#dcfce7', text: '#16a34a' }
    : portalStatus === 'Cancelled'
    ? { bg: '#fee2e2', text: '#dc2626' }
    : { bg: '#f3f4f6', text: '#6b7280' };

  const handleRefresh = async () => {
    if (!slug || refreshing) return;
    setRefreshing(true);
    try {
      const cached = sessionStorage.getItem(`${SESSION_KEY_PREFIX}${slug}`);
      if (!cached) return;
      const { tenant: cachedTenant } = JSON.parse(cached);
      const { data, error } = await supabase.rpc('tenant_portal_verify', {
        p_slug: slug,
        p_first_name: cachedTenant.first_name,
        p_last_name: cachedTenant.last_name,
      });
      if (!error && data?.error === 'plan_required') {
        sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${slug}`);
        setPortalData(null);
        setState('verifying');
        setPopup(PLAN_REQUIRED_POPUP);
      } else if (!error && !data?.error) {
        sessionStorage.setItem(`${SESSION_KEY_PREFIX}${slug}`, JSON.stringify(data));
        applyPortalData(data);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const fetchPaymentsPage = async (page: number) => {
    if (!tenant || paymentsPageLoading) return;
    setPaymentsPageLoading(true);
    try {
      const { data, error } = await supabase.rpc('tenant_portal_get_payments', {
        p_slug: slug,
        p_first_name: tenant.first_name,
        p_last_name: tenant.last_name,
        p_page: page,
        p_page_size: PAGE_SIZE,
      });
      if (!error && data && !data.error) {
        const result = data as PageResult<PaymentRecord>;
        setPayments(result.items);
        setPaymentsTotalCount(result.total_count);
        setPaymentsPage(page);
      }
    } finally {
      setPaymentsPageLoading(false);
    }
  };

  const fetchMaintenancePage = async (page: number) => {
    if (!tenant || maintenancePageLoading) return;
    setMaintenancePageLoading(true);
    try {
      const { data, error } = await supabase.rpc('tenant_portal_get_maintenance_requests', {
        p_slug: slug,
        p_first_name: tenant.first_name,
        p_last_name: tenant.last_name,
        p_page: page,
        p_page_size: PAGE_SIZE,
      });
      if (!error && data && !data.error) {
        const result = data as PageResult<MaintenanceRequestRecord>;
        setMaintenanceRequests(result.items);
        setMaintenanceTotalCount(result.total_count);
        setMaintenancePage(page);
      }
    } finally {
      setMaintenancePageLoading(false);
    }
  };

  const renderPagination = (
    page: number,
    totalCount: number,
    loading: boolean,
    onPageChange: (page: number) => void
  ) => {
    const totalPages = Math.max(Math.ceil(totalCount / PAGE_SIZE), 1);
    if (totalPages <= 1) return null;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '8px',
      }}>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={loading || page <= 1}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            backgroundColor: '#fff',
            color: page <= 1 ? '#d1d5db' : 'var(--etenant-primary)',
            cursor: loading || page <= 1 ? 'not-allowed' : 'pointer',
          }}
        >
          <i className="fas fa-chevron-left" style={{ fontSize: '12px' }} />
        </button>
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600 }}>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={loading || page >= totalPages}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            backgroundColor: '#fff',
            color: page >= totalPages ? '#d1d5db' : 'var(--etenant-primary)',
            cursor: loading || page >= totalPages ? 'not-allowed' : 'pointer',
          }}
        >
          <i className="fas fa-chevron-right" style={{ fontSize: '12px' }} />
        </button>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', fontFamily: "'DM Sans', sans-serif" }}>
      <SEO title="Tenant Portal" noindex />
      <header className="header-nav">
        <nav className="navbar navbar-expand-lg">
          <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <a className="navbar-brand" href="/">
              <img src={logoDark} alt="eTENANT Logo" style={{ height: '60px' }} />
            </a>
            {state === 'verified' && (
              <span style={{
                fontSize: '15px',
                fontWeight: 700,
                color: 'var(--etenant-primary)',
              }}>
                Tenant Portal
              </span>
            )}
          </div>
        </nav>
      </header>

      {/* Popup modal */}
      {popup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '14px',
            padding: '32px 24px',
            maxWidth: '360px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <i className={popup.icon} style={{ fontSize: '44px', color: popup.iconColor, marginBottom: '16px', display: 'block' }} />
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>
              {popup.title}
            </h3>
            <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '24px' }}>
              {popup.message}
            </p>
            <button
              onClick={() => setPopup(null)}
              style={{
                padding: '10px 28px',
                backgroundColor: 'var(--etenant-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Photo lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
            cursor: 'zoom-out',
          }}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              color: '#fff',
              fontSize: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="fas fa-xmark" />
          </button>
          <img
            src={lightboxUrl}
            alt="Maintenance request"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              cursor: 'default',
            }}
          />
        </div>
      )}

      <div style={{ maxWidth: '520px', margin: '0 auto', padding: '32px 16px' }}>

        {/* VERIFICATION FORM */}
        {state === 'verifying' && (
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            padding: '32px 24px',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <i className="fas fa-shield-halved" style={{ fontSize: '36px', color: 'var(--etenant-primary)', marginBottom: '12px', display: 'block' }} />
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '6px' }}>
                Tenant Portal
              </h2>
              <p style={{ color: '#6b7280', fontSize: '13px' }}>
                Enter your first and last name to access your payment history.
              </p>
            </div>

            <form onSubmit={handleVerify}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={attempts >= MAX_ATTEMPTS}
                  placeholder="Enter your first name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={attempts >= MAX_ATTEMPTS}
                  placeholder="Enter your last name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

<button
                type="submit"
                disabled={loading || attempts >= MAX_ATTEMPTS}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--etenant-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: loading || attempts >= MAX_ATTEMPTS ? 'not-allowed' : 'pointer',
                  opacity: loading || attempts >= MAX_ATTEMPTS ? 0.6 : 1,
                }}
              >
                {loading ? 'Verifying…' : 'Access My Portal'}
              </button>
            </form>
          </div>
        )}

        {/* VERIFIED VIEW */}
        {state === 'verified' && portalData && (
          <div>
            {/* Agreement summary card */}
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Welcome back,</p>
                  <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1f2937', margin: 0 }}>
                    {tenant?.first_name} {tenant?.last_name}
                  </h2>
                </div>
                <span style={{
                  backgroundColor: portalStatusColor.bg,
                  color: portalStatusColor.text,
                  fontSize: '12px',
                  fontWeight: 800,
                  padding: '4px 10px',
                  borderRadius: '20px',
                }}>
                  {portalStatus}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {agreement?.rental_agreement_no && (
                  <div>
                    <p style={{ fontSize: '11px', color: '#1f2937', fontWeight: 800, margin: '0 0 2px' }}>Agreement No.</p>
                    <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>
                      {agreement.rental_agreement_no}
                    </p>
                  </div>
                )}
                <div>
                  <p style={{ fontSize: '11px', color: '#1f2937', fontWeight: 800, margin: '0 0 2px' }}>Monthly Rent</p>
                  <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>
                    {formatCurrency(agreement?.monthly_rent ?? 0)}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#1f2937', fontWeight: 800, margin: '0 0 2px' }}>Start Date</p>
                  <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>
                    {formatDate(agreement?.start_date ?? null)}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#1f2937', fontWeight: 800, margin: '0 0 2px' }}>End Date</p>
                  <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>
                    {formatDate(agreement?.end_date ?? null)}
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', backgroundColor: '#e5e7eb', borderRadius: '10px', padding: '4px' }}>
              {(['payments', 'maintenance', 'checklists'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    backgroundColor: activeTab === tab ? '#fff' : 'transparent',
                    color: activeTab === tab ? 'var(--etenant-primary)' : '#6b7280',
                    boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  {tab === 'payments' ? 'Payment History' : tab === 'maintenance' ? 'Maintenance' : 'Checklists'}
                </button>
              ))}
            </div>

            {/* Payment History Tab */}
            {activeTab === 'payments' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                    Showing {payments.length} of {paymentsTotalCount} {paymentsTotalCount === 1 ? 'record' : 'records'}
                  </span>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    title="Refresh"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: refreshing ? 'not-allowed' : 'pointer',
                      color: 'var(--etenant-primary)',
                      fontSize: '13px',
                      fontWeight: 700,
                      padding: 0,
                      opacity: refreshing ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <i className={`fas fa-rotate-right${refreshing ? ' fa-spin' : ''}`} />
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
                {payments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                    <i className="fas fa-receipt" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '12px', display: 'block' }} />
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No payments recorded yet.</p>
                  </div>
                ) : (
                  payments.map((payment) => {
                    const s = statusColors[payment.status] ?? statusColors.pending;
                    const isPaid = payment.status === 'paid';
                    const isCancelled = payment.status === 'cancelled';
                    return (
                      <div
                        key={payment.id}
                        style={{
                          backgroundColor: '#fff',
                          borderRadius: '10px',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                          padding: '14px 16px',
                          marginBottom: '10px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', margin: '0 0 2px' }}>
                              {toTitleCase(payment.payment_type)}
                            </p>
                            <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                              {formatDate(payment.created_at)}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{
                              fontSize: '15px',
                              fontWeight: 700,
                              color: isCancelled ? '#9ca3af' : isPaid ? '#16a34a' : '#1f2937',
                              textDecoration: isCancelled ? 'line-through' : 'none',
                              margin: '0 0 4px',
                            }}>
                              {isPaid && <span style={{ fontSize: '12px' }}>+</span>}
                              {formatCurrency(payment.amount)}
                            </p>
                            <span style={{
                              backgroundColor: s.bg,
                              color: s.text,
                              fontSize: '11px',
                              fontWeight: 800,
                              padding: '2px 8px',
                              borderRadius: '20px',
                            }}>
                              {s.label}
                            </span>
                          </div>
                        </div>
                        {payment.due_date && (
                          <p style={{ fontSize: '11px', color: '#9ca3af', margin: '8px 0 0' }}>
                            <i className="fas fa-calendar-day" style={{ marginRight: '4px' }} />
                            Due: {formatDate(payment.due_date)}
                          </p>
                        )}
                        {payment.notes && (
                          <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0', borderTop: '1px solid #f3f4f6', paddingTop: '6px' }}>
                            {payment.notes}
                          </p>
                        )}
                      </div>
                    );
                  })
                )}
                {renderPagination(paymentsPage, paymentsTotalCount, paymentsPageLoading, fetchPaymentsPage)}
              </div>
            )}

            {/* Maintenance Tab */}
            {activeTab === 'maintenance' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                    Showing {maintenanceRequests.length} of {maintenanceTotalCount} {maintenanceTotalCount === 1 ? 'request' : 'requests'}
                  </span>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    title="Refresh"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: refreshing ? 'not-allowed' : 'pointer',
                      color: 'var(--etenant-primary)',
                      fontSize: '13px',
                      fontWeight: 700,
                      padding: 0,
                      opacity: refreshing ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <i className={`fas fa-rotate-right${refreshing ? ' fa-spin' : ''}`} />
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
                {maintenanceRequests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                    <i className="fas fa-wrench" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '12px', display: 'block' }} />
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No maintenance requests recorded yet.</p>
                  </div>
                ) : (
                  maintenanceRequests.map((request) => {
                    const s = maintenanceStatusColors[request.status] ?? maintenanceStatusColors.pending;
                    const icon = maintenanceCategoryIcons[request.category] ?? 'fa-wrench';
                    const hasActualCost = (request.actual_cost ?? 0) > 0;
                    const highlightedCost = hasActualCost ? (request.actual_cost ?? 0) : (request.estimated_cost ?? 0);
                    return (
                      <div
                        key={request.id}
                        style={{
                          backgroundColor: '#fff',
                          borderRadius: '10px',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                          padding: '14px 16px',
                          marginBottom: '10px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          {/* Thumbnail */}
                          <div
                            onClick={request.photo_storage_path ? () => setLightboxUrl(getMaintenancePhotoUrl(request.photo_storage_path!)) : undefined}
                            style={{
                              width: '52px',
                              height: '52px',
                              flexShrink: 0,
                              borderRadius: '10px',
                              overflow: 'hidden',
                              backgroundColor: 'rgba(0,0,0,0.05)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: request.photo_storage_path ? 'pointer' : 'default',
                            }}
                          >
                            {request.photo_storage_path ? (
                              <img
                                src={getMaintenancePhotoUrl(request.photo_storage_path)}
                                alt={toTitleCase(request.category)}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <i className={`fas ${icon}`} style={{ fontSize: '18px', color: 'var(--etenant-primary)', opacity: 0.5 }} />
                            )}
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', margin: '0 0 2px' }}>
                                  {toTitleCase(request.category)}
                                </p>
                                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                                  {formatDate(request.created_at)}
                                </p>
                              </div>
                              <div style={{ textAlign: 'right', marginLeft: '8px' }}>
                                <p style={{ fontSize: '15px', fontWeight: 700, color: '#1f2937', margin: '0 0 4px' }}>
                                  {formatCurrency(highlightedCost)}
                                </p>
                                <span style={{
                                  backgroundColor: s.bg,
                                  color: s.text,
                                  fontSize: '11px',
                                  fontWeight: 800,
                                  padding: '2px 8px',
                                  borderRadius: '20px',
                                }}>
                                  {s.label}
                                </span>
                              </div>
                            </div>
                            <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0', borderTop: '1px solid #f3f4f6', paddingTop: '6px' }}>
                              {request.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                {renderPagination(maintenancePage, maintenanceTotalCount, maintenancePageLoading, fetchMaintenancePage)}
              </div>
            )}

            {/* Checklists Tab */}
            {activeTab === 'checklists' && (
              <div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  {(['move_in', 'move_out'] as const).map((sub) => (
                    <button
                      key={sub}
                      onClick={() => setChecklistSubTab(sub)}
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '20px',
                        border: checklistSubTab === sub ? '1px solid var(--etenant-primary)' : '1px solid #e5e7eb',
                        backgroundColor: checklistSubTab === sub ? 'var(--etenant-primary)' : '#fff',
                        color: checklistSubTab === sub ? '#fff' : '#6b7280',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {sub === 'move_in' ? 'Move-In' : 'Move-Out'}
                    </button>
                  ))}
                </div>

                {!activeChecklist ? (
                  <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                    <i className="fas fa-list-check" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '12px', display: 'block' }} />
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No checklist available.</p>
                  </div>
                ) : activeChecklist.items.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                    <i className="fas fa-list-check" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '12px', display: 'block' }} />
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No items in this checklist.</p>
                  </div>
                ) : (
                  activeChecklist.items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        backgroundColor: '#fff',
                        borderRadius: '10px',
                        border: `1px solid ${item.is_checked ? '#16a34a' : '#e5e7eb'}`,
                        padding: '12px 14px',
                        marginBottom: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                      }}
                    >
                      <i
                        className={`fas ${item.is_checked ? 'fa-square-check' : 'fa-square'}`}
                        style={{ fontSize: '18px', color: item.is_checked ? '#16a34a' : '#d1d5db', flexShrink: 0 }}
                      />
                      <span style={{
                        fontSize: '13px',
                        color: '#1f2937',
                        textDecoration: item.is_checked ? 'line-through' : 'none',
                      }}>
                        {item.section}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        <p style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', marginTop: '32px' }}>
          © 2026 eTENANT. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default TenantPortal;
